import { flow } from 'fp-ts/function';
import { compact } from 'lodash';
import { equals, get, getOr, includes, isNil, map, mapValues, negate, size } from 'lodash/fp';
import { createSelector } from 'reselect';

import { PaymentCardType, PaymentGatewayModel, PaymentGatewayType } from '@mews/enums';
import { getOrEmptyArray, getStringProp } from '@mews/fp';
import { multiplyNumbersAsDecimals } from '@mews/framework';
import { convertSupportedPaymentMethods, PaymentCard } from '@mews/framework/payments';
import { composeProps } from '@mews/framework/redux';

import { parentSelector } from '../../selectors/parentSelector';
import { ApplicationState } from '../../types';
import { WithEnterpriseId } from '../../types/selectors/selectorPropTypes';
import { selectedEnterpriseIdPropsExtender } from '../resourceOrder/selectors';
import { PaymentConfiguration, PaymentConfigurations, SurchargeConfiguration } from './types';

const getPaymentConfigurations = createSelector<ApplicationState, ApplicationState, PaymentConfigurations>(
    parentSelector,
    get('paymentConfigurations')
);

const getEnterprisePaymentConfiguration = createSelector<
    ApplicationState,
    WithEnterpriseId,
    string,
    PaymentConfigurations,
    PaymentConfiguration | undefined
>(getStringProp('enterpriseId'), getPaymentConfigurations, get);

const getSelectedEnterprisePaymentConfiguration = composeProps(
    getEnterprisePaymentConfiguration,
    selectedEnterpriseIdPropsExtender
);

const getSelectedEnterpriseSurchargeConfiguration = flow(
    getSelectedEnterprisePaymentConfiguration,
    get('surchargeConfiguration')
);

const getSurchargeFees = flow(
    getSelectedEnterpriseSurchargeConfiguration,
    get<SurchargeConfiguration, 'surchargeFees'>('surchargeFees')
);

export const hasSelectedEnterpriseSurcharge = flow(
    getSelectedEnterpriseSurchargeConfiguration,
    get('surchargeServiceId'),
    negate(isNil)
);

export const getFormattedSurchargeFees = flow(
    getSurchargeFees,
    mapValues((value: number) => multiplyNumbersAsDecimals(value, 100))
);

export const isSelectedEnterprisePaymentConfigurationFetching = createSelector(
    getSelectedEnterprisePaymentConfiguration,
    get<PaymentConfiguration, 'isFetching'>('isFetching')
);

export const hasSelectedEnterprisePaymentConfigurationFailed = createSelector(
    getSelectedEnterprisePaymentConfiguration,
    get<PaymentConfiguration, 'hasFailed'>('hasFailed')
);

export const shouldGetPaymentConfiguration = createSelector(
    getSelectedEnterprisePaymentConfiguration,
    isSelectedEnterprisePaymentConfigurationFetching,
    hasSelectedEnterprisePaymentConfigurationFailed,
    (paymentConfiguration, isFetching, hasFailed) =>
        isFetching !== true && (size(paymentConfiguration) === 0 || hasFailed)
);

const getEnterprisePaymentGateway = flow(getEnterprisePaymentConfiguration, get('paymentGateway'));

export const hasEnterpriseMewsMerchant = flow(getEnterprisePaymentGateway, get('isMerchant'));

const getEnterprisePaymentGatewayType = flow(
    getEnterprisePaymentGateway,
    getOr(PaymentGatewayType.None, 'paymentGatewayType')
);

export const hasEnterpriseNonePaymentGatewayType = flow(
    getEnterprisePaymentGatewayType,
    equals(PaymentGatewayType.None)
);

export const hasEnterprisePciProxyStorage = flow(getEnterprisePaymentGatewayType, equals(PaymentGatewayType.PciProxy));

export const getEnterpriseSupportedPaymentCardTypes = flow(
    getEnterprisePaymentGateway,
    getOrEmptyArray('supportedCreditCardTypes')
);

const getEnterpriseSupportedPaymentCardImageTypes = createSelector(
    getEnterpriseSupportedPaymentCardTypes,
    flow(
        map((type: PaymentCardType) => PaymentCard.getTypeFromPaymentCard(type)),
        compact
    )
);

export const getSelectedEnterpriseSupportedPaymentCardImageTypes = composeProps(
    getEnterpriseSupportedPaymentCardImageTypes,
    selectedEnterpriseIdPropsExtender
);

export const getEnterpriseSupportedPaymentMethods = createSelector(getEnterprisePaymentGateway, (gateway) =>
    gateway != null ? convertSupportedPaymentMethods(gateway.supportedPaymentMethods) : []
);

const getEnterprisePaymentGatewayModel = flow(getEnterprisePaymentGateway, get('model'));

export const isEnterpriseModelMerchantOfRecord = flow(
    getEnterprisePaymentGatewayModel,
    equals(PaymentGatewayModel.MerchantOfRecord)
);

export const getSelectedEnterprisePaymentGateway = composeProps(
    getEnterprisePaymentGateway,
    selectedEnterpriseIdPropsExtender
);

export const getSelectedEnterprisePaymentGatewayPublicKey = flow(getSelectedEnterprisePaymentGateway, get('publicKey'));

export const getSelectedEnterprisePaymentGatewayDefaultCurrencyCode = flow(
    getSelectedEnterprisePaymentGateway,
    get('defaultCurrencyCode')
);

const getSelectedEnterprisePaymentGatewayAccounts = flow(getSelectedEnterprisePaymentGateway, get('accounts'));

const getSelectedEnterprisePaymentGatewaySupportedCurrencyCodes = createSelector(
    getSelectedEnterprisePaymentGatewayAccounts,
    map('currencyCode')
);

export const getPreferredOrDefaultCurrencyCode = createSelector(
    getStringProp('preferredCurrencyCode'),
    getSelectedEnterprisePaymentGatewaySupportedCurrencyCodes,
    getSelectedEnterprisePaymentGatewayDefaultCurrencyCode,
    (preferredCurrencyCode, supportedCurrencyCodes, defaultCurrencyCode) =>
        includes(preferredCurrencyCode, supportedCurrencyCodes) ? preferredCurrencyCode : defaultCurrencyCode
);
